import { FC, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Spacer } from 'jbc-front/components/presenters/Spacer';
import { Label } from 'jbc-front/components/presenters/form/Label';
import { Input } from 'jbc-front/components/presenters/form/Input';
import { ErrorMessage } from 'jbc-front/components/presenters/form/ErrorMessage';
import { BoxDouble } from '@/components/Box';
import { Headding, Item, Panel } from '@/components/PrimaryPanel';
import { Select } from '@/components/react-hook-form/Select';
import { DateField } from '@/components/react-hook-form/DateField';
import { usePrevious } from '@/hooks/usePrevious';
import { Office, Prefecture, BusinessType } from '../../query';
import { Schema } from '../../schema';
import styles from './BasicInformation.scss';

interface BasicInformationProps {
  mode: 'create' | 'edit';
  loading: boolean;
  offices?: Office[];
  prefectures?: Prefecture[];
  businessTypes?: BusinessType[];
}

export const BasicInformation: FC<BasicInformationProps> = ({ mode, loading, offices, prefectures, businessTypes }) => {
  const {
    register,
    watch,
    setValue,
    formState: { errors }
  } = useFormContext<Schema>();

  const officeId = watch('officeId');
  const prevOfficeId = usePrevious(officeId);
  useEffect(() => {
    if (officeId && prevOfficeId !== officeId) {
      const office = offices?.find(office => office.id === officeId);
      if (!office) return;

      const laborInfo = office.laborContactInfo || office.socialContactInfo;
      const ownerInfo = office.ownerContactInfo || office.socialContactInfo;
      if (!laborInfo || !ownerInfo) return;

      setValue('officeSnapshot.name', laborInfo.name);
      setValue('officeSnapshot.postcode0', laborInfo.postcode0);
      setValue('officeSnapshot.postcode1', laborInfo.postcode1);
      setValue('officeSnapshot.prefectureId', laborInfo.prefecture?.id);
      setValue('officeSnapshot.city', laborInfo.city);
      setValue('officeSnapshot.houseNumber', laborInfo.houseNumber);
      setValue('officeSnapshot.building', laborInfo.building);
      setValue('officeSnapshot.phoneNumber0', laborInfo.phoneNumber0);
      setValue('officeSnapshot.phoneNumber1', laborInfo.phoneNumber1);
      setValue('officeSnapshot.phoneNumber2', laborInfo.phoneNumber2);
      setValue('officeSnapshot.laborInsuranceNumber0', office.laborInsuranceNumber0);
      setValue('officeSnapshot.laborInsuranceNumber1', office.laborInsuranceNumber1);
      setValue('officeSnapshot.laborInsuranceNumber2', office.laborInsuranceNumber2);
      setValue('officeSnapshot.laborInsuranceNumber3', office.laborInsuranceNumber3);
      setValue('officeSnapshot.laborInsuranceNumber4', office.laborInsuranceNumber4);
      setValue('officeSnapshot.repPositionName', ownerInfo.repPositionName);
      setValue('officeSnapshot.repLastName', ownerInfo.repLastName);
      setValue('officeSnapshot.repFirstName', ownerInfo.repFirstName);
      setValue(
        'officeSnapshot.laborStandardsInspectionOfficeLShortName',
        office.laborStandardsInspectionOfficeL?.shortName
      );
    } else if (prevOfficeId && !officeId) {
      setValue('officeSnapshot.name', '');
      setValue('officeSnapshot.postcode0', '');
      setValue('officeSnapshot.postcode1', '');
      setValue('officeSnapshot.prefectureId', '');
      setValue('officeSnapshot.city', '');
      setValue('officeSnapshot.houseNumber', '');
      setValue('officeSnapshot.building', '');
      setValue('officeSnapshot.phoneNumber0', '');
      setValue('officeSnapshot.phoneNumber1', '');
      setValue('officeSnapshot.phoneNumber2', '');
      setValue('officeSnapshot.laborInsuranceNumber0', '');
      setValue('officeSnapshot.laborInsuranceNumber1', '');
      setValue('officeSnapshot.laborInsuranceNumber2', '');
      setValue('officeSnapshot.laborInsuranceNumber3', '');
      setValue('officeSnapshot.laborInsuranceNumber4', '');
      setValue('officeSnapshot.repPositionName', '');
      setValue('officeSnapshot.repLastName', '');
      setValue('officeSnapshot.repFirstName', '');
      setValue('officeSnapshot.laborStandardsInspectionOfficeLShortName', '');
    }
  }, [prevOfficeId, officeId, offices, setValue]);

  const officeOptions = offices
    ? [{ value: '', label: '' }, ...offices.map(office => ({ value: office.id, label: office.name }))]
    : [{ value: '', label: '' }];
  const prefectureOptions = prefectures ? prefectures.map(pref => ({ value: pref.id, label: pref.name })) : [];
  const businessTypeOptions = businessTypes
    ? [
        { value: '', label: '' },
        ...businessTypes.map(businessType => ({ value: businessType.id, label: businessType.name }))
      ]
    : [{ value: '', label: '' }];

  return (
    <Panel>
      <Headding>基本情報</Headding>
      <Item>
        <div>
          <Label>適用事業所</Label>
          <Spacer size={8} />
          <Select name="officeId" options={officeOptions} disabled={loading} isLoading={loading} />
          {errors.officeId?.message && <ErrorMessage>{errors.officeId.message}</ErrorMessage>}
        </div>

        <div className="u-mt20">
          <Label>事業所の名称</Label>
          <Spacer size={8} />
          <Input {...register('officeSnapshot.name')} disabled />
        </div>

        <div className="u-mt20">
          <Label>郵便番号</Label>
          <Spacer size={8} />
          <div className={styles.postcode}>
            〒<Input className={styles.postcode0} {...register('officeSnapshot.postcode0')} disabled />-
            <Input className={styles.postcode1} {...register('officeSnapshot.postcode1')} disabled />
          </div>
        </div>

        <div className="u-mt20">
          <BoxDouble>
            <div>
              <Label>都道府県</Label>
              <Spacer size={8} />
              <Select name="officeSnapshot.prefectureId" options={prefectureOptions} disabled isLoading={loading} />
            </div>
            <div>
              <Label>市区町村</Label>
              <Spacer size={8} />
              <Input {...register('officeSnapshot.city')} disabled />
            </div>
          </BoxDouble>
        </div>

        <div className="u-mt20">
          <BoxDouble>
            <div>
              <Label>丁目番地号</Label>
              <Spacer size={8} />
              <Input {...register('officeSnapshot.houseNumber')} disabled />
            </div>
            <div>
              <Label>建物名</Label>
              <Spacer size={8} />
              <Input {...register('officeSnapshot.building')} disabled />
            </div>
          </BoxDouble>
        </div>

        <div className="u-mt20">
          <Label>電話番号</Label>
          <Spacer size={8} />
          <div className={styles.phoneNumber}>
            <Input className={styles.phoneNumberSegment} {...register('officeSnapshot.phoneNumber0')} disabled />-
            <Input className={styles.phoneNumberSegment} {...register('officeSnapshot.phoneNumber1')} disabled />-
            <Input className={styles.phoneNumberSegment} {...register('officeSnapshot.phoneNumber2')} disabled />
          </div>
        </div>

        <div className="u-mt20">
          <Label>事業の種類</Label>
          <Spacer size={8} />
          <Select name="businessTypeId" options={businessTypeOptions} disabled={loading} isLoading={loading} />
          {errors.businessTypeId?.message && <ErrorMessage>{errors.businessTypeId.message}</ErrorMessage>}
        </div>

        <div className="u-mt20">
          <Label>報告期間</Label>
          <Spacer size={8} />
          <BoxDouble>
            <div>
              <DateField name="startOn" />
              {errors.startOn?.message && <ErrorMessage>{errors.startOn.message}</ErrorMessage>}
            </div>
            <div>
              <DateField name="endOn" />
              {errors.endOn?.message && <ErrorMessage>{errors.endOn.message}</ErrorMessage>}
            </div>
          </BoxDouble>
        </div>

        {mode === 'edit' && (
          <div className="u-mt20">
            <Label>報告回数</Label>
            <Spacer size={8} />
            <BoxDouble>
              <Input {...register('reportNumber')} />
            </BoxDouble>
            {errors.reportNumber?.message && <ErrorMessage>{errors.reportNumber.message}</ErrorMessage>}
          </div>
        )}
      </Item>
    </Panel>
  );
};
